import { useEffect, useRef, useState, useMemo } from "react";
import { Play, Pause, Volume2, VolumeX, Sparkles, Calendar, ArrowRight, RotateCcw } from "lucide-react";

type Lang = "en" | "fr" | "es";

interface Box {
  label: "Au" | "H2O" | "Anomaly" | "Lineament";
  confidence: number;
  x: number; y: number; w: number; h: number;
}
interface Entry {
  t: number;
  phase: string;
  caption: string;
  caption_fr: string;
  boxes: Box[];
}

const LABEL_COLORS: Record<Box["label"], { stroke: string; fill: string; text: string }> = {
  Au:        { stroke: "#FFD24A", fill: "rgba(255,210,74,0.10)",  text: "#FFD24A" },
  H2O:       { stroke: "#5BC0FF", fill: "rgba(91,192,255,0.10)",  text: "#5BC0FF" },
  Anomaly:   { stroke: "#FF6B6B", fill: "rgba(255,107,107,0.10)", text: "#FF6B6B" },
  Lineament: { stroke: "#A78BFA", fill: "rgba(167,139,250,0.10)", text: "#A78BFA" },
};

const PHASE_LABEL: Record<string, { en: string; fr: string; es: string }> = {
  INIT:         { en: "INITIALIZING",      fr: "INITIALISATION",        es: "INICIALIZANDO" },
  SCAN:         { en: "SATELLITE SCAN",    fr: "SCAN SATELLITE",        es: "ESCANEO SATELITAL" },
  FUSION:       { en: "DATA FUSION",       fr: "FUSION DE DONNÉES",     es: "FUSIÓN DE DATOS" },
  ANALYSIS:     { en: "AI ANALYSIS",       fr: "ANALYSE IA",            es: "ANÁLISIS IA" },
  DETECT_GOLD:  { en: "GOLD DETECTION",    fr: "DÉTECTION OR",          es: "DETECCIÓN ORO" },
  DETECT_WATER: { en: "WATER DETECTION",   fr: "DÉTECTION EAU",         es: "DETECCIÓN AGUA" },
  RANK:         { en: "TARGET RANKING",    fr: "CLASSEMENT CIBLES",     es: "RANKING OBJETIVOS" },
  REPORT:       { en: "REPORT GENERATION", fr: "GÉNÉRATION RAPPORT",    es: "GENERACIÓN INFORME" },
};

interface Props {
  src?: string;
  analysisSrc?: string;
  lang?: Lang;
  onBookDemo?: () => void;
}

export default function SocratDemoVideo({
  src = "/socrat-demo.mp4",
  analysisSrc = "/socrat-demo-analysis.json",
  lang = "en",
  onBookDemo,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [timeline, setTimeline] = useState<Entry[]>([]);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [duration, setDuration] = useState(0);
  const [ended, setEnded] = useState(false);

  const L = {
    en: { ended: 'Demo complete', cta: 'Book a live SOCRAT demo', sub: 'Get a 30-min walkthrough on your own perimeter.', replay: 'Replay', book: 'Book a Live Demo', bookSub: 'See SOCRAT live on your perimeter — 30 min, free briefing.' },
    fr: { ended: 'Démo terminée', cta: 'Réserver une démo SOCRAT en direct', sub: 'Session de 30 min sur votre périmètre.', replay: 'Rejouer', book: 'Réserver une démo en direct', bookSub: 'Voyez SOCRAT sur votre périmètre — 30 min, briefing gratuit.' },
    es: { ended: 'Demo completa', cta: 'Reservar una demo SOCRAT en vivo', sub: 'Sesión de 30 min sobre tu perímetro.', replay: 'Reproducir', book: 'Reservar demo en vivo', bookSub: 'Mira SOCRAT en tu perímetro — 30 min, briefing gratuito.' },
  }[lang];

  useEffect(() => {
    fetch(analysisSrc)
      .then((r) => r.json())
      .then((d) => setTimeline(d.timeline ?? []))
      .catch(() => setTimeline([]));
  }, [analysisSrc]);

  // Find current entry: largest t <= time
  const current = useMemo(() => {
    if (!timeline.length) return null;
    let cur: Entry | null = null;
    for (const e of timeline) {
      if (e.t <= time) cur = e;
      else break;
    }
    return cur;
  }, [timeline, time]);

  // Smoothly interpolate boxes between entries for less jumpiness
  const interpolated = useMemo(() => {
    if (!timeline.length) return [] as Box[];
    const i = timeline.findIndex((e) => e.t > time);
    const prev = i === -1 ? timeline[timeline.length - 1] : timeline[Math.max(0, i - 1)];
    return prev.boxes;
  }, [timeline, time]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setTime(v.currentTime);
    const onPlay = () => { setPlaying(true); setEnded(false); };
    const onPause = () => setPlaying(false);
    const onMeta = () => setDuration(v.duration || 0);
    const onEnded = () => { setPlaying(false); setEnded(true); };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("ended", onEnded);
    };
  }, []);

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    setEnded(false);
    v.play();
  };

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const phase = current?.phase ?? "INIT";
  const phaseTxt = (PHASE_LABEL[phase] ?? PHASE_LABEL.INIT)[lang] ?? PHASE_LABEL[phase]?.en ?? phase;
  const caption = current ? (lang === "fr" ? current.caption_fr : current.caption) : "";

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      {/* Header chip */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-sovereign-gold/40 rounded-full bg-sovereign-navy/40 backdrop-blur-sm">
          <Sparkles className="h-3.5 w-3.5 text-sovereign-gold" />
          <span className="text-xs font-mono tracking-[0.2em] text-sovereign-gold uppercase">
            Live SOCRAT Demo · Analyzed by Gemini 2.5
          </span>
        </div>
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-sovereign-gold/30 shadow-2xl shadow-sovereign-gold/10 bg-black group">
        {/* Video */}
        <video
          ref={videoRef}
          src={src}
          className="w-full h-auto block aspect-video"
          playsInline
          muted={muted}
          onClick={toggle}
        />

        {/* HUD overlay */}
        <div className="pointer-events-none absolute inset-0">
          {/* Scanline */}
          <div
            className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-sovereign-gold/80 to-transparent"
            style={{
              top: `${duration ? (time / duration) * 100 : 0}%`,
              boxShadow: "0 0 12px rgba(212,175,55,0.6)",
              transition: "top 120ms linear",
            }}
          />

          {/* Top-left phase badge */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-black/70 border border-sovereign-gold/50 backdrop-blur-md">
              <span className="h-2 w-2 rounded-full bg-sovereign-gold animate-pulse" />
              <span className="text-[10px] sm:text-xs font-mono tracking-[0.15em] text-sovereign-gold uppercase">
                {phaseTxt}
              </span>
            </span>
          </div>

          {/* Top-right meta */}
          <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
            <span className="text-[10px] font-mono text-white/80 bg-black/60 border border-white/20 px-2 py-1 rounded-md">
              SOCRAT v2.4 · LIVE
            </span>
            <span className="text-[10px] font-mono text-white/60 bg-black/60 px-2 py-1 rounded-md">
              t = {time.toFixed(1)}s
            </span>
          </div>

          {/* Privacy blur over browser tabs in final segment */}
          {time >= 240 && (
            <div
              className="absolute top-0 left-0 right-0 pointer-events-none"
              style={{
                height: "9%",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                background: "rgba(10,10,20,0.35)",
              }}
            />
          )}

          {/* Bounding boxes */}
          {interpolated.map((b, idx) => {
            const c = LABEL_COLORS[b.label] ?? LABEL_COLORS.Anomaly;
            return (
              <div
                key={`${b.label}-${idx}`}
                className="absolute border-2 rounded-sm transition-all duration-300"
                style={{
                  left: `${b.x * 100}%`,
                  top: `${b.y * 100}%`,
                  width: `${b.w * 100}%`,
                  height: `${b.h * 100}%`,
                  borderColor: c.stroke,
                  background: c.fill,
                  boxShadow: `0 0 14px ${c.stroke}55, inset 0 0 14px ${c.stroke}22`,
                }}
              >
                {/* corner ticks */}
                <span className="absolute -top-px -left-px w-3 h-3 border-t-2 border-l-2" style={{ borderColor: c.stroke }} />
                <span className="absolute -top-px -right-px w-3 h-3 border-t-2 border-r-2" style={{ borderColor: c.stroke }} />
                <span className="absolute -bottom-px -left-px w-3 h-3 border-b-2 border-l-2" style={{ borderColor: c.stroke }} />
                <span className="absolute -bottom-px -right-px w-3 h-3 border-b-2 border-r-2" style={{ borderColor: c.stroke }} />
                <span
                  className="absolute -top-6 left-0 text-[10px] font-mono tracking-wider px-1.5 py-0.5 rounded-sm bg-black/85 whitespace-nowrap"
                  style={{ color: c.text, border: `1px solid ${c.stroke}80` }}
                >
                  {b.label} · {(b.confidence * 100).toFixed(0)}%
                </span>
              </div>
            );
          })}

          {/* Subtitle */}
          {caption && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-16 sm:bottom-20 max-w-[90%] w-fit">
              <div className="px-4 py-2.5 rounded-lg bg-black/80 backdrop-blur-md border border-sovereign-gold/40 shadow-lg">
                <p className="text-white text-sm sm:text-base font-medium leading-snug text-center">
                  {caption}
                </p>
              </div>
            </div>
          )}

          {/* End-of-video CTA overlay */}
          {ended && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/85 via-sovereign-navy/85 to-black/90 backdrop-blur-sm pointer-events-auto animate-in fade-in duration-300">
              <div className="text-center px-6 max-w-lg">
                <div className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] text-sovereign-gold border border-sovereign-gold/40 rounded-full px-3 py-1 mb-4">
                  <Sparkles className="h-3 w-3" /> {L.ended}
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 leading-tight">{L.cta}</h3>
                <p className="text-white/80 text-sm sm:text-base mb-6">{L.sub}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={onBookDemo}
                    className="bg-sovereign-gold text-sovereign-navy px-6 py-3 rounded-lg font-bold inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform shadow-lg shadow-sovereign-gold/30"
                  >
                    <Calendar className="h-4 w-4" /> {L.book} <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={replay}
                    className="border-2 border-white/40 text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" /> {L.replay}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Controls bar */}
        <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 bg-gradient-to-t from-black/95 via-black/70 to-transparent flex items-center gap-3">
          <button
            onClick={toggle}
            className="h-9 w-9 rounded-full bg-sovereign-gold text-sovereign-navy flex items-center justify-center hover:scale-105 transition-transform pointer-events-auto"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
          </button>
          <span className="text-[11px] font-mono text-white/80 tabular-nums">
            {fmt(time)} / {fmt(duration)}
          </span>
          <div
            className="flex-1 h-1 bg-white/15 rounded-full overflow-hidden cursor-pointer pointer-events-auto"
            onClick={(e) => {
              const rect = (e.target as HTMLDivElement).getBoundingClientRect();
              const ratio = (e.clientX - rect.left) / rect.width;
              if (videoRef.current && duration) videoRef.current.currentTime = ratio * duration;
            }}
          >
            <div
              className="h-full bg-sovereign-gold"
              style={{ width: `${duration ? (time / duration) * 100 : 0}%` }}
            />
          </div>
          <button
            onClick={() => setMuted((m) => !m)}
            className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center pointer-events-auto"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 mt-4 text-xs font-mono">
        {(["Au", "H2O", "Anomaly", "Lineament"] as Box["label"][]).map((l) => {
          const c = LABEL_COLORS[l];
          return (
            <div key={l} className="flex items-center gap-2 px-2.5 py-1 rounded-md border border-white/15 bg-sovereign-navy/40">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: c.stroke }} />
              <span className="text-white/80 tracking-wider">{l}</span>
            </div>
          );
        })}
      </div>

      {/* Book a Live Demo CTA below video */}
      <div className="mt-6 rounded-2xl border border-sovereign-gold/30 bg-gradient-to-r from-sovereign-navy/60 via-sovereign-navy/40 to-sovereign-navy/60 backdrop-blur p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-center sm:text-left">
          <div className="text-[10px] font-mono tracking-[0.25em] text-sovereign-gold mb-1">SOCRAT · LIVE BRIEFING</div>
          <p className="text-white font-bold text-base sm:text-lg leading-snug">{L.bookSub}</p>
        </div>
        <button
          onClick={onBookDemo}
          className="bg-sovereign-gold text-sovereign-navy px-6 py-3 rounded-lg font-bold inline-flex items-center justify-center gap-2 whitespace-nowrap hover:scale-[1.02] transition-transform shadow-lg shadow-sovereign-gold/20"
        >
          <Calendar className="h-4 w-4" /> {L.book} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
